import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ProjectDetails } from '@/types';

const STORAGE_KEY = 'scheduler-project-selector-state';
const MAX_RECENT_PROJECTS = 10;

interface ProjectSelectorState {
  favourites: string[];
  recentlyUsed: Array<{ id: string; timestamp: number }>;
}

export function useProjectSelector(projects: ProjectDetails[]) {
  const [state, setState] = useState<ProjectSelectorState>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          favourites: parsed.favourites || [],
          recentlyUsed: parsed.recentlyUsed || [],
        };
      }
    } catch (error) {
      console.error('Failed to load project selector state:', error);
    }
    return { favourites: [], recentlyUsed: [] };
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save project selector state:', error);
    }
  }, [state]);

  const toggleFavourite = useCallback((projectId: string) => {
    setState((prev) => {
      const isFav = prev.favourites.includes(projectId);
      return {
        ...prev,
        favourites: isFav
          ? prev.favourites.filter((id) => String(id) !== String(projectId))
          : [...prev.favourites, projectId],
      };
    });
  }, []);

  const recordProjectAccess = useCallback((projectId: string) => {
    setState((prev) => {
      const filtered = prev.recentlyUsed.filter((item) => String(item.id) !== String(projectId));
      const updated = [
        { id: projectId, timestamp: Date.now() },
        ...filtered,
      ].slice(0, MAX_RECENT_PROJECTS);
      return {
        ...prev,
        recentlyUsed: updated,
      };
    });
  }, []);

  const enrichedProjects = useMemo(() => {
    return projects.map((project) => ({
      ...project,
      isFavourite: state.favourites.includes(String(project.id)),
            lastAccessedDate: state.recentlyUsed.find((item) => String(item.id) === String(project.id))
              ? new Date(state.recentlyUsed.find((item) => String(item.id) === String(project.id))!.timestamp)
        : undefined,
    }));
  }, [projects, state.favourites, state.recentlyUsed]);

  const favouriteProjects = useMemo(() => {
    return enrichedProjects
      .filter((p) => p.isFavourite)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [enrichedProjects]);

  const recentProjects = useMemo(() => {
    const recentIds = state.recentlyUsed.map((item) => item.id);
    
    // Get projects that have been accessed
    const accessedProjects = enrichedProjects
      .filter((p) => recentIds.includes(String(p.id)))
      .sort((a, b) => {
        const aTime = a.lastAccessedDate?.getTime() || 0;
        const bTime = b.lastAccessedDate?.getTime() || 0;
        return bTime - aTime;
      });
    
    // If we have fewer than 5 accessed projects, fill with other projects (alphabetically)
    if (accessedProjects.length < 5) {
      const accessedIds = new Set(accessedProjects.map(p => String(p.id)));
      const otherProjects = enrichedProjects
        .filter(p => !accessedIds.has(String(p.id)))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      
      return [...accessedProjects, ...otherProjects].slice(0, 5);
    }
    
    return accessedProjects.slice(0, 5); // Always show exactly 5 recent projects
  }, [enrichedProjects, state.recentlyUsed]);

  return {
    enrichedProjects,
    favouriteProjects,
    recentProjects,
    toggleFavourite,
    recordProjectAccess,
    isFavourite: (projectId: string) => state.favourites.includes(projectId),
  };
}
